import React, { useMemo } from 'react';
import { Armchair, DoorOpen, Coffee, Baby } from 'lucide-react';
import { money } from './duffelShared';

const PAX_COLOURS = ['bg-sky-500', 'bg-emerald-500', 'bg-violet-500', 'bg-pink-500', 'bg-amber-500', 'bg-teal-500'];

const Facility = ({ el }) => {
  const Icon = el.type === 'exit_row' ? DoorOpen : el.type === 'bassinet' ? Baby : Coffee;
  if (el.type === 'empty') return <span className="h-9 w-9" />;
  return (
    <span className="h-9 w-9 flex items-center justify-center text-slate-300" title={el.type.replace('_', ' ')}>
      <Icon className="h-4 w-4" />
    </span>
  );
};

const Seat = ({ el, service, state, paxIndex, onPick }) => {
  const base = 'relative h-9 w-9 rounded-t-lg rounded-b-sm text-[10px] font-bold flex flex-col items-center justify-center transition-transform';
  if (state === 'taken') {
    return <span className={`${base} ${PAX_COLOURS[paxIndex % PAX_COLOURS.length]} text-white`} title={`${el.designator} — selected for another passenger`} data-testid={`seat-${el.designator}`}>{paxIndex + 1}</span>;
  }
  if (state === 'unavailable') {
    return <span className={`${base} bg-slate-200 text-slate-400 cursor-not-allowed`} title={`${el.designator} — not available`} data-testid={`seat-${el.designator}`}>×</span>;
  }
  return (
    <button
      type="button"
      onClick={() => onPick(service, el)}
      title={`${el.designator}${el.name ? ' · ' + el.name : ''} · ${money(service.amount)}`}
      data-testid={`seat-${el.designator}`}
      className={`${base} border-2 ${state === 'selected' ? 'bg-[#FF6600] border-[#FF6600] text-white scale-110 shadow' : 'bg-white border-orange-300 text-slate-700 hover:bg-orange-50 hover:scale-105'}`}
    >
      <span>{el.designator}</span>
      <span className={`text-[9px] font-medium ${state === 'selected' ? 'text-white' : 'text-slate-500'}`}>{service.amount ? `£${Math.round(service.amount)}` : 'free'}</span>
    </button>
  );
};

export const DuffelSeatMap = ({ map, passengerId, passengers, selectedBySeat, onPick }) => {
  const prices = useMemo(() => {
    const set = new Set();
    map.cabins.forEach((c) => c.rows.forEach((r) => r.sections.forEach((s) => s.elements.forEach((e) => {
      const svc = e.services?.find((x) => x.passenger_id === passengerId);
      if (svc) set.add(svc.amount);
    }))));
    return [...set].sort((a, b) => a - b);
  }, [map, passengerId]);

  return (
    <div data-testid={`seat-map-${map.segment_id}`}>
      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600 mb-4">
        <span className="flex items-center gap-1"><span className="h-4 w-4 rounded-t bg-white border-2 border-orange-300" />Available{prices.length ? ` (${prices.map((p) => money(p)).join(' / ')})` : ''}</span>
        <span className="flex items-center gap-1"><span className="h-4 w-4 rounded-t bg-[#FF6600]" />Your seat</span>
        <span className="flex items-center gap-1"><span className="h-4 w-4 rounded-t bg-slate-200" />Unavailable</span>
        {passengers.map((p, i) => <span key={p.id} className="flex items-center gap-1"><span className={`h-4 w-4 rounded-t ${PAX_COLOURS[i % PAX_COLOURS.length]}`} />Passenger {i + 1}</span>)}
      </div>

      <div className="mx-auto w-fit rounded-[2.5rem] border-2 border-slate-200 bg-slate-50 px-6 py-8">
        <div className="flex justify-center mb-4 text-slate-400"><Armchair className="h-5 w-5" /><span className="text-xs ml-2 self-center">Front of aircraft</span></div>
        {map.cabins.map((cabin, ci) => (
          <div key={ci} className="space-y-1.5">
            {cabin.rows.map((row, ri) => {
              const onWing = cabin.wings && ri >= cabin.wings.first_row_index && ri <= cabin.wings.last_row_index;
              return (
                <div key={ri} className="flex items-center gap-1">
                  <span className={`w-1.5 h-9 rounded ${onWing ? 'bg-slate-300' : 'bg-transparent'}`} />
                  {row.sections.map((sec, si) => (
                    <React.Fragment key={si}>
                      {si > 0 && <span className="w-6 text-center text-[10px] text-slate-400">{sec.elements.find((e) => e.designator)?.designator?.match(/\d+/)?.[0] || ''}</span>}
                      <div className="flex gap-1">
                        {sec.elements.map((el, ei) => {
                          if (el.type !== 'seat') return <Facility key={ei} el={el} />;
                          const svc = el.services?.find((x) => x.passenger_id === passengerId);
                          const taken = selectedBySeat[el.designator];
                          const state = taken && taken.passengerId !== passengerId ? 'taken' : taken ? 'selected' : svc ? 'available' : 'unavailable';
                          return <Seat key={ei} el={el} service={svc} state={state} paxIndex={taken ? passengers.findIndex((p) => p.id === taken.passengerId) : 0} onPick={onPick} />;
                        })}
                      </div>
                    </React.Fragment>
                  ))}
                  <span className={`w-1.5 h-9 rounded ${onWing ? 'bg-slate-300' : 'bg-transparent'}`} />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
